import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  GraphicEq,
  Tune,
  Bedtime,
  Widgets,
  Headphones,
  Lock
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SITE_ORIGIN, usePageSeo } from './usePageSeo';

const PAGE_TITLE = 'White Noise: Premium Serenity | iPhone Sleep Sounds';
const PAGE_DESCRIPTION = 'White Noise: Premium Serenity is an iPhone sleep sounds app. Mix white, pink, and brown noise with rain, ocean, and piano, then fade out with a sleep timer.';
const PAGE_IMAGE = `${SITE_ORIGIN}/hush/icon.png`;

const features = [
  {
    icon: <GraphicEq />,
    title: 'A mixer, not a single loop',
    body: 'Layer color noise, nature, and tones. White, pink, brown, blue, violet, and gray noise sit next to rain, ocean, piano, fan, and more.'
  },
  {
    icon: <Tune />,
    title: 'Your mix, saved',
    body: 'Set each sound’s level, add a custom Hz tone, and save the mix. Play on launch brings the last one back when you open the app.'
  },
  {
    icon: <Bedtime />,
    title: 'Built for the night',
    body: 'A sleep timer can fade the mix out. A bedtime reminder, night mode, and a nightstand clock keep the phone useful after the lights go down.'
  },
  {
    icon: <Headphones />,
    title: 'Plays where you listen',
    body: 'Audio keeps going in the background. Route it with AirPlay, mix it with Music, or keep playing if headphones disconnect.'
  },
  {
    icon: <Widgets />,
    title: 'Control from the Home Screen',
    body: 'Widgets and a sleep-timer Live Activity put play, pause, and the countdown outside the app.'
  },
  {
    icon: <Lock />,
    title: 'Stays on your iPhone',
    body: 'Mixes and settings are stored on the device. White Noise: Premium Serenity does not require an account, and it does not use ads or tracking.'
  }
];

const Hush = () => {
  const navigate = useNavigate();

  usePageSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: '/white-noise',
    image: PAGE_IMAGE,
    keywords: 'white noise app, pink noise, brown noise, sleep sounds, rain sounds, focus sounds, iPhone, sound mixer, iOS',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'White Noise: Premium Serenity',
      operatingSystem: 'iOS',
      applicationCategory: 'HealthApplication',
      description: PAGE_DESCRIPTION,
      url: `${SITE_ORIGIN}/white-noise`,
      image: PAGE_IMAGE,
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '0',
        highPrice: '19.99',
        priceCurrency: 'USD',
        offerCount: '3'
      },
      author: {
        '@type': 'Person',
        name: 'Michael R. Ellis',
        jobTitle: 'iOS Software Engineer',
        url: `${SITE_ORIGIN}/`
      }
    }
  });

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Portfolio
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '24px',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.08)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'center' },
              gap: { xs: 4, md: 6 }
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
              <Box
                component="img"
                src="/hush/icon.png"
                alt="White Noise: Premium Serenity app icon"
                sx={{
                  width: { xs: 72, sm: 88 },
                  height: { xs: 72, sm: 88 },
                  borderRadius: '22px',
                  mb: 2
                }}
              />
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.85rem', sm: '2.4rem' },
                  color: '#2c3e50',
                  '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                }}
              >
                White Noise: Premium Serenity
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 1.5,
                  fontWeight: 500,
                  color: 'text.secondary',
                  '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                }}
              >
                Soundscapes for sleep and focus
              </Typography>
              <Typography
                sx={{
                  maxWidth: 560,
                  mt: 2,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  '@media (prefers-color-scheme: dark)': { color: '#c8c8c8' }
                }}
              >
                A white noise app for sleep and focus. Stack white, pink, and brown noise
                with piano, rain, or a custom tone, then let a sleep timer fade it out.
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                justifyContent={{ xs: 'center', md: 'flex-start' }}
                sx={{ mt: 3 }}
              >
                {['iOS', 'Sleep timer', 'On-device'].map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: '#e8ecf4',
                      color: '#4a5366',
                      '@media (prefers-color-scheme: dark)': {
                        backgroundColor: '#333',
                        color: '#eee'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
            <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src="/hush/now-playing.gif"
                alt="White Noise: Premium Serenity now playing, with white noise and peaceful piano in a mix"
                width={295}
                height={640}
                decoding="async"
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  height: 'auto',
                  display: 'block',
                  borderRadius: '28px',
                  boxShadow: '0 16px 48px rgba(0,0,0,0.28)'
                }}
              />
            </Box>
          </Box>
        </Paper>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            mt: { xs: 5, sm: 7 },
            mb: 3,
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            color: '#2c3e50',
            '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
          }}
        >
          What it does
        </Typography>
        <Grid container spacing={2.5}>
          {features.map((feature) => (
            <Grid item key={feature.title} xs={12} sm={6} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: '20px',
                  backgroundColor: 'white',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  '@media (prefers-color-scheme: dark)': {
                    backgroundColor: '#1a1a1a',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
                  }
                }}
              >
                <Box sx={{ color: '#4a5366', mb: 1.5, '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' } }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#2c3e50',
                    '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' }
                  }}
                >
                  {feature.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: { xs: 4, sm: 6 },
            p: { xs: 3, sm: 4 },
            borderRadius: '20px',
            textAlign: 'center',
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            '@media (prefers-color-scheme: dark)': {
              backgroundColor: '#1a1a1a',
              boxShadow: '0 8px 32px rgba(255,255,255,0.06)'
            }
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: '#2c3e50',
              '@media (prefers-color-scheme: dark)': { color: '#ffffff' }
            }}
          >
            Support
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2, '@media (prefers-color-scheme: dark)': { color: '#b0b0b0' } }}>
            Questions about the app:{' '}
            <Box
              component="a"
              href="mailto:Contact@MichaelRobertEllis.com"
              sx={{
                color: '#4a5366',
                '@media (prefers-color-scheme: dark)': { color: '#a5b4fc' }
              }}
            >
              Contact@MichaelRobertEllis.com
            </Box>
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center" alignItems="center">
            <Button component={RouterLink} to="/white-noise-privacy-policy">
              Privacy Policy
            </Button>
            <Button component={RouterLink} to="/white-noise-terms">
              Terms and Conditions
            </Button>
          </Stack>
        </Paper>
      </Container>
    </>
  );
};

export default Hush;
